import { publicApiWithoutAuth } from '@/shared/lib/api/clients'

import { PUBLIC_QUEST_TOTEM_HEADER } from '../constants/totem'

import type {
  EventBranding,
  Quest,
  QuestEligibleReward,
  QuestFormInfo,
  QuestLockedRecord,
  QuestRun,
  QuizPlayResponse,
  QuizStepMeta,
  QuizStepSelection
} from './types'

const questsBasePath = (eventSlug: string) =>
  `/public/digital_products/${eventSlug}/quests`

const questPath = (eventSlug: string, questUid: string) =>
  `${questsBasePath(eventSlug)}/${questUid}`

const questRunsBasePath = (eventSlug: string, questUid: string) =>
  `${questPath(eventSlug, questUid)}/quest_runs`

const questRunPath = (eventSlug: string, questUid: string, runId: number) =>
  `${questRunsBasePath(eventSlug, questUid)}/${runId}`

const totemHeaders = (token: string | null) =>
  token ? { headers: { [PUBLIC_QUEST_TOTEM_HEADER]: token } } : { headers: {} }

export const fetchQuests = async (eventSlug: string): Promise<Quest[]> => {
  const { data } = await publicApiWithoutAuth.get(questsBasePath(eventSlug))
  return data?.records ?? []
}

export const fetchQuest = async (
  eventSlug: string,
  questUid: string,
  totemToken: string | null
): Promise<{ quest: Quest | null; lockedRecord: QuestLockedRecord | null }> => {
  const { data } = await publicApiWithoutAuth.get(
    questPath(eventSlug, questUid),
    totemHeaders(totemToken)
  )

  if (data?.unlock_required || data?.record?.unlock_required) {
    return { quest: null, lockedRecord: data.record ?? data }
  }

  return { quest: data?.record ?? null, lockedRecord: null }
}

export const unlockTotem = async (
  eventSlug: string,
  questUid: string,
  pattern: string
): Promise<{ ok: boolean; unlock_token?: string }> => {
  const { data } = await publicApiWithoutAuth.post(
    `${questPath(eventSlug, questUid)}/unlock_totem`,
    { pattern }
  )
  return data
}

export const fetchEventBranding = async (eventSlug: string): Promise<EventBranding | null> => {
  const { data } = await publicApiWithoutAuth.get(
    `/public/digital_products/${eventSlug}?include=logo`
  )
  return data?.record ?? data ?? null
}

export const createRun = async (
  eventSlug: string,
  questUid: string,
  record: {
    email?: string
    registration_number?: string
    info_responses: Record<string, unknown>
  },
  totemToken: string | null
): Promise<QuestRun> => {
  const { data } = await publicApiWithoutAuth.post(
    questRunsBasePath(eventSlug, questUid),
    { record },
    totemHeaders(totemToken)
  )
  return data?.record ?? data
}

export const fetchRun = async (
  eventSlug: string,
  questUid: string,
  runId: number
): Promise<QuestRun> => {
  const { data } = await publicApiWithoutAuth.get(questRunPath(eventSlug, questUid, runId))
  return data?.record ?? data
}

export const fetchQuizPlay = async (
  eventSlug: string,
  questUid: string,
  runId: number
): Promise<QuizPlayResponse> => {
  const { data } = await publicApiWithoutAuth.get(
    `${questRunPath(eventSlug, questUid, runId)}/quiz_play`
  )
  return data
}

export const submitQuizStep = async (
  eventSlug: string,
  questUid: string,
  runId: number,
  selection: QuizStepSelection
): Promise<{ record: QuestRun; quiz_step: QuizStepMeta }> => {
  const { data } = await publicApiWithoutAuth.post(
    `${questRunPath(eventSlug, questUid, runId)}/quiz_step`,
    { record: selection }
  )
  return data
}

export const submitQuizComplete = async (
  eventSlug: string,
  questUid: string,
  runId: number,
  selections: QuizStepSelection[]
): Promise<QuestRun> => {
  const { data } = await publicApiWithoutAuth.post(
    `${questRunPath(eventSlug, questUid, runId)}/quiz_complete`,
    { record: { selections } }
  )
  return data?.record ?? data
}

export const finishRun = async (
  eventSlug: string,
  questUid: string,
  runId: number
): Promise<QuestRun> => {
  const { data } = await publicApiWithoutAuth.post(
    `${questRunPath(eventSlug, questUid, runId)}/finish`,
    { record: { score: 0, challenge_responses: {} } }
  )
  return data?.record ?? data
}

export const chooseReward = async (
  eventSlug: string,
  questUid: string,
  runId: number,
  selectedRewardId: number | null
): Promise<QuestRun> => {
  const record = selectedRewardId ? { selected_reward_id: selectedRewardId } : {}
  const { data } = await publicApiWithoutAuth.post(
    `${questRunPath(eventSlug, questUid, runId)}/choose_reward`,
    { record }
  )
  return data?.record ?? data
}

export type { QuestFormInfo, QuestEligibleReward }
